import { BaseCard, CardBody, Css, formatDate, Palette } from "@homebound/beam";
import { Fragment } from "react";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { assertNever, formatList } from "src/utils";
import { SelectConfigurationStep_StructureFragment } from "../1.SelectConfigurationStep.generated";
import {
  ApplyConfigurationCard_AggregateVersionFragment,
  ApplyConfigurationCard_CheckoutConfigFragment,
  ApplyConfigurationCard_ProductOfferingFragment,
  ApplyConfigurationCard_ProjectReadyPlanConfigFragment,
} from "./ApplyConfigurationCard.generated";

type ApplyConfigurationCardConfig =
  ApplyConfigurationCard_CheckoutConfigFragment | ApplyConfigurationCard_ProjectReadyPlanConfigFragment;

type ApplyConfigurationCardProps = {
  structure: SelectConfigurationStep_StructureFragment;
  readyPlanConfig: ApplyConfigurationCardConfig;
  onClick?: () => void;
  onRemove?: () => void;
};

/** Displays a ready plan configuration's cover photo, plan and design selections. */
export function ApplyConfigurationCard(props: ApplyConfigurationCardProps) {
  const { structure, readyPlanConfig, onClick, onRemove } = props;
  const { name, updatedAt, readyPlan } = readyPlanConfig;
  const { aggregateVersion, productOffering, exteriorOptionNames, interiorOptionNames } = resolveConfigFields(
    structure,
    readyPlanConfig,
  );
  const fields = [
    { label: "Plan", value: readyPlan?.name },
    { label: "Exterior", value: readyPlan ? formatList(exteriorOptionNames) : "-" },
    { label: "Interior", value: readyPlan ? formatList(interiorOptionNames) : "-" },
    { label: "Version", value: aggregateVersion?.displayVersion ?? "-" },
    { label: "Updated", value: readyPlan ? formatDate(updatedAt.toPlainDate(), "shortDate") : "-" },
  ];
  return (
    <BaseCard
      imgSrc={productOffering?.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG}
      imgAlt={readyPlan?.name ?? ""}
      onClick={onClick}
      action={onRemove ? { icon: "trash", label: "Replace", onClick: onRemove, bgColor: Palette.White } : undefined}
    >
      <CardBody title={name ?? "Current Configuration"}>
        <ConfigurationFields fields={fields} />
      </CardBody>
    </BaseCard>
  );
}

function ConfigurationFields(props: { fields: { label: string; value: string | undefined }[] }) {
  const { fields } = props;
  return (
    <dl css={Css.dg.gtc("1fr 1fr").rg1.cg2.mb0.mta.$}>
      {fields.map((field) => (
        <Fragment key={field.label}>
          <dt css={Css.smSb.$}>{field.label}</dt>
          <dd css={Css.sm.m0.$}>{field.value ?? "-"}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/**
 * Resolves the aggregateVersion and productOffering fields for a configuration.
 * A checkout config covers both sides of the lot, so an ADU structure reads the ADU side of it.
 */
function resolveConfigFields(
  structure: SelectConfigurationStep_StructureFragment,
  readyPlanConfig: ApplyConfigurationCardConfig,
): {
  aggregateVersion: ApplyConfigurationCard_AggregateVersionFragment | undefined | null;
  productOffering: ApplyConfigurationCard_ProductOfferingFragment | undefined | null;
  exteriorOptionNames: string[];
  interiorOptionNames: string[];
} {
  switch (readyPlanConfig.__typename) {
    case "CheckoutConfig":
      if (structure.isAdu) {
        return {
          aggregateVersion: readyPlanConfig.aduAggregateVersion,
          productOffering: readyPlanConfig.aduProductOffering,
          exteriorOptionNames: readyPlanConfig.aduExteriorOptionNames,
          interiorOptionNames: readyPlanConfig.aduInteriorOptionNames,
        };
      } else {
        return {
          aggregateVersion: readyPlanConfig.aggregateVersion,
          productOffering: readyPlanConfig.productOffering,
          exteriorOptionNames: readyPlanConfig.mhExteriorOptionNames,
          interiorOptionNames: readyPlanConfig.mhInteriorOptionNames,
        };
      }
    case "ProjectReadyPlanConfig":
      return {
        aggregateVersion: readyPlanConfig.aggregateVersion,
        productOffering: readyPlanConfig.productOffering,
        exteriorOptionNames: readyPlanConfig.exteriorOptionNames,
        interiorOptionNames: readyPlanConfig.interiorOptionNames,
      };
    default:
      assertNever(readyPlanConfig.__typename as never);
  }
}
